/* eslint-disable import/no-cycle */
import { DataProvider } from "@shared/constants/common";
import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";
import StripePaymentGateway from "../../gateways/StripePaymentGateway/StripePaymentGateway";
import PaymentGatewayFactory from "../../PaymentGatewayFactory";
import { type PaymentManagerConfig } from "../../PaymentManager";
import useBasketPaymentService, {
  PaymentServiceBasket,
} from "../../services/useBasketPaymentService";
import useInvoicePaymentService from "../../services/useInvoicePaymentService";
import { PurchaseOptsType } from "../../types";
import PaymentHandler from "../PaymentHandler";

import { PaymentConfigurationError } from "../../utils/errors/PaymentConfigurationError";
import { PaymentInitializationError } from "../../utils/errors/PaymentInitializationError";
import { PaymentProcessingError } from "../../utils/errors/PaymentProcessingError";
import PaymentGateway from "../../gateways/PaymentGateway";

/**
 * The default payment handler for Affirm payments
 */
class AffirmPaymentHandler extends PaymentHandler {
  public static handlerName = "AffirmPaymentHandler";

  HAS_ASYNCHRONOUS_CONFIRMATION = true;
  HAS_ASYNCHRONOUS_CAPTURE = true;

  is_initialized = false;
  gateway: StripePaymentGateway | null = null;

  constructor(
    config: PaymentManagerConfig,
    basket: PaymentServiceBasket,
    servicesContext: {
      basketPaymentService: ReturnType<typeof useBasketPaymentService>;
      invoicePaymentService: ReturnType<typeof useInvoicePaymentService>;
    }
  ) {
    super(config, basket, servicesContext);

    const selectedGateway = PaymentGatewayFactory.getOrCreateGateway(
      config,
      basket,
      servicesContext
    );

    // Check that the gateway that was selected is supported by this handler
    if (!(selectedGateway instanceof StripePaymentGateway)) {
      throw new PaymentConfigurationError(
        `Affirm payment handler only supports Stripe payment gateway, but received ${selectedGateway.constructor.name}. Please check your payment gateway configuration.`,
        {
          metadata: {
            supportedGateways: ["StripePaymentGateway"],
            receivedGateway: selectedGateway.constructor.name,
          },
        }
      );
    }

    this.gateway = selectedGateway;
  }

  async initialize(basket: PaymentServiceBasket) {
    if (!this.gateway) {
      throw new PaymentInitializationError(
        "Cannot initialize Affirm payment handler because the payment gateway is not set. This indicates a configuration issue.",
        {
          metadata: {
            operation: "initialize",
          },
        }
      );
    }

    await this.gateway.initialize(basket);

    this.is_initialized = true;
    return this;
  }

  async processCheckout(
    _: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType
  ) {
    if (!this.gateway) {
      throw new PaymentProcessingError(
        "Cannot process Affirm payment because the payment gateway is not initialized",
        {
          metadata: {
            operation: "processCheckout",
          },
          userMessage: "Please try refreshing the page.",
        }
      );
    }

    if (this.gateway instanceof StripePaymentGateway) {
      await this.gateway.confirmPayment(
        PAYMENT_MANAGER_CONFIRMATION_STRATEGY.BUY_NOW_PAY_LATER,
        basketType,
        basket,
        purchaseOpts
      );
    } else {
      throw new PaymentProcessingError(
        "Cannot process Affirm payment because the gateway is not a supported type for this handler",
        {
          metadata: {
            operation: "processCheckout",
            gatewayType:
              (this.gateway as PaymentGateway)?.constructor?.name || "unknown",
          },
          userMessage:
            "Your payment could not be processed. Please try a different payment method.",
        }
      );
    }

    // Add other gateways here

    return this;
  }

  async postProcessCheckout() {
    if (!this.gateway) {
      throw new PaymentProcessingError(
        "Cannot complete Affirm payment processing because the payment gateway is not initialized",
        {
          metadata: {
            operation: "postProcessCheckout",
          },
          userMessage:
            "Your payment could not be processed. Please try a different payment method.",
        }
      );
    }

    return this.gateway.postProcessCheckout();
  }
}

export default AffirmPaymentHandler;
